import { useCallback, useState } from "react";
import Swal from "sweetalert2";

export const useReturnCart = (t) => {
    const [cart, setCart] = useState([]);

    const addProductToCart = useCallback(
        (product) => {
            // console.log("Adding product to return cart:", product);

            // Handle both stock products and sale items
            const batches = product?.stockData || product?.batchList || [];

            if (!batches.length) {
                Swal.fire({
                    icon: "info",
                    title: t("No batches found"),
                    text: t("No stock batches available for {{name}}. Please add stock before returning.", {
                        name: product?.brandName || product?.medicineName || "product",
                    }),
                    confirmButtonText: t("OK"),
                });
                return;
            }

            const batch = batches[0];
            setCart((prev) => {
                const existingIndex = prev.findIndex(
                    (item) => item._id === product._id && item.selectedBatchId === batch._id
                );

                if (existingIndex !== -1) {
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        quantity: updated[existingIndex].quantity + 1,
                    };
                    return updated;
                }
                return [
                    ...prev,
                    {
                        ...product,
                        batchList: batches,
                        selectedBatchId: batch._id,
                        batchNumber: batch.batchNumber || product.batchNumber,
                        expiryDate: batch.expiryDate,
                        salePrice: batch.unitPricePerMedicine || product.salePrice || product.price || 0,
                        quantity: product.quantity || 0,
                    },
                ];
            });
        },
        [t]
    );

    const handleBatchChange = useCallback((itemId, batchId) => {
        setCart((prev) =>
            prev.map((item) => {
                if (item._id === itemId) {
                    const batch = item.batchList?.find((b) => b._id === batchId);
                    if (!batch) return item;
                    return {
                        ...item,
                        selectedBatchId: batchId,
                        batchNumber: batch.batchNumber,
                        expiryDate: batch.expiryDate,
                        salePrice: batch.unitPricePerMedicine || batch.price || item.salePrice,
                        quantity: 1,
                    };
                }
                return item;
            })
        );
    }, []);

    const updateItemField = useCallback((itemId, batchId, field, value) => {
        setCart((prev) =>
            prev.map((item) =>
                item._id === itemId && item.selectedBatchId === batchId
                    ? { ...item, [field]: Number(value) }
                    : item
            )
        );
    }, []);

    const handleQuantityChange = useCallback((itemId, batchId, quantity) => {
        setCart((prev) =>
            prev.map((item) =>
                item._id === itemId && item.selectedBatchId === batchId
                    ? { ...item, quantity: Math.max(1, Number(quantity) || 1) }
                    : item
            )
        );
    }, []);

    const removeItem = useCallback((itemId, batchId) => {
        setCart((prev) => prev.filter((item) => !(item._id === itemId && item.selectedBatchId === batchId)));
    }, []);

    const handleTaxChange = useCallback((itemId, batchId, value) => {
        const parsed = parseFloat(value);
        const normalized = Number.isFinite(parsed)
            ? Math.min(100, Math.max(0, Math.round(parsed * 100) / 100))
            : 0;

        setCart((prevCart) =>
            prevCart.map((item) =>
                item._id === itemId && item.selectedBatchId === batchId
                    ? { ...item, taxPercentage: normalized }
                    : item
            )
        );
    }, []);

    const resetCart = useCallback(() => {
        setCart([]);
    }, []);

    return {
        cart,
        addProductToCart,
        handleBatchChange,
        updateItemField,
        handleQuantityChange,
        removeItem,
        resetCart,
        setCart,
        handleTaxChange,
    };
};
